// @ts-strict-ignore
import React, { useState } from 'react';
import type { Option } from '@usealloy/component-library';
import {
  Box, Icon, IconName, MultiSelect, Text, Tooltip,
} from '@usealloy/component-library';
import { useAppSelector } from '~/store/reduxHooks';
import type { RecurringWorkflowInfo } from '~/dashboard/views/workflows/store/engine/types';
import { selectAllWorkflows } from '~/dashboard/views/workflows/store/redux/selectors';
import { WEBHOOK_KEY } from '../../../constants';
import { ColumnBox } from '../../styles';
import type { ServiceOption, ReadOnlyProp } from '../../types';
import { createServiceOption } from '../../../serviceOptionHelpers';

export interface RecurringWebhookContainerProps extends ReadOnlyProp {
  initialSelectedRecurringWorkflow: ServiceOption;
  recurringWorkflowOptions: RecurringWorkflowInfo[];
  onChange: (updatedData: ServiceOption) => void;
}

export default function RecurringWebhookContainer({
  initialSelectedRecurringWorkflow,
  recurringWorkflowOptions,
  onChange,
  readOnly,
}: RecurringWebhookContainerProps) {
  const allWorkflowsByAppId = useAppSelector(selectAllWorkflows({ byApplicationId: true }));
  const [selectedChild, setSelectedChild] = useState<ServiceOption>(
    initialSelectedRecurringWorkflow);
  const noSelectedChildOption: Option = { label: 'Select', value: null };

  const activeRecurringWorkflows = recurringWorkflowOptions.filter(option => (
    !(allWorkflowsByAppId[option.application_id]).archived));

  const buildMultiSelectWorkflowOptions = (options: RecurringWorkflowInfo[]) => (
    options.map(workflow => ({
      label: workflow.name,
      value: `${workflow.application_id}`,
    } as Option)));

  const selectionOptions = [
    noSelectedChildOption,
    ...buildMultiSelectWorkflowOptions(activeRecurringWorkflows),
  ];

  const getSelectedOption = () => {
    // Some workflows that were copied/pasted across customers incorrectly copied the configured recurring workflow for the
    // service actions, despite the new customer not having access to that recurring workflow. We wrap the following
    // in a try catch block to fail gracefully in those cases.
    try {
      return {
        label: recurringWorkflowOptions.find(
          workflow => workflow.application_id === Number(selectedChild.value)).name,
        value: selectedChild.value,
      } as Option;
    } catch {
      return noSelectedChildOption;
    }
  };

  const handleChange = (selectedOption: Option) => {
    if (selectedOption === noSelectedChildOption) {
      setSelectedChild(undefined);
      onChange(null);
      return;
    }
    if (initialSelectedRecurringWorkflow
            && (selectedOption.value === initialSelectedRecurringWorkflow.value)) {
      setSelectedChild(initialSelectedRecurringWorkflow);
      onChange(initialSelectedRecurringWorkflow);
      return;
    }
    const serviceOption = createServiceOption({
      key: WEBHOOK_KEY,
      value: selectedOption.value,
    });
    setSelectedChild(serviceOption);
    onChange(serviceOption);
  };

  return (
    <ColumnBox>
      <Box style={{ justifyContent: 'flex-start' }}>
        <Text>Recurring Webhook Workflow</Text>
        <Tooltip
          content="Designates a recurring workflow to monitor existing entities."
        >
          <Icon icon={IconName.Question} />
        </Tooltip>
      </Box>
      <MultiSelect
        isMulti={false}
        onChange={handleChange}
        options={selectionOptions}
        selectedOptions={selectedChild
          ? getSelectedOption()
          : noSelectedChildOption}
        isDisabled={readOnly}
      />
    </ColumnBox>
  );
}
